import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import DashboardLayout from './viewDashboards/DashboardLayout';
import CustomDashboardRoutes from './userDashboards/CustomDashboardRoutes';
import PerformanceUtils from './viewDashboards/utils/performanceUtils';
import LandingPageLayout from './landingPage/LandingPageLayout';
import DashboardAudit from './dashboardAudit/DashboardAudit';
import PrintLayout from './viewDashboards/export/printPreview/PrintLayout';
import { createCanvas } from './viewDashboards/export/exportUtils';
import { LabelContextProvider } from './shared/hooks/labelContext';
import { FeatureTokenContextProvider } from './shared/hooks/featureTokensContext';
import 'smartsuite-ui/dist/smartsuite-ui.css';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

export default function AppRouter(props) {
    useEffect(() => {
        createCanvas();
        PerformanceUtils.setLogUri(props.uriConstants.logMessageUri);
    }, [props.uriConstants.logMessageUri]);

    return (
        <QueryClientProvider client={queryClient}>
            <LabelContextProvider siteLanguage={props.siteLanguage}>
                <FeatureTokenContextProvider
                    getUserFeatureTokensUri={
                        props.uriConstants.getUserFeatureTokensUri
                    }
                    ssiSystemFeature={props.ssiSystemFeature}
                >
                    <BrowserRouter basename={props.baseName}>
                        <Switch>
                            <Route path="/Audit/:dashboardKey?">
                                <DashboardAudit
                                    uriConstants={props.uriConstants}
                                />
                            </Route>
                            <Route
                                path="/LandingPage"
                                render={({ location }) => (
                                    <LandingPageLayout
                                        assets={props.assets}
                                        customizations={props.customizations}
                                        favoriteUtilities={
                                            props.favoriteUtilities
                                        }
                                        location={location}
                                        uriConstants={props.uriConstants}
                                    />
                                )}
                            />
                            <Route
                                path="/Print/:printViewMode/:dashboardKey/:viewKey"
                                render={({ location, match }) => (
                                    <PrintLayout
                                        assets={props.assets}
                                        customizations={props.customizations}
                                        dashboardKey={match.params.dashboardKey}
                                        favoriteUtilities={
                                            props.favoriteUtilities
                                        }
                                        isPrintView={true}
                                        location={location}
                                        printViewMode={
                                            match.params.printViewMode
                                        }
                                        uriConstants={props.uriConstants}
                                        viewKey={match.params.viewKey}
                                    />
                                )}
                            />
                            <Route
                                path="/Custom"
                                render={({ match }) => (
                                    <CustomDashboardRoutes
                                        assets={props.assets}
                                        baseName={props.baseName}
                                        customizations={props.customizations}
                                        favoriteUtilities={
                                            props.favoriteUtilities
                                        }
                                        menuUtilities={props.menuUtilities}
                                        path={match.path}
                                        uriConstants={props.uriConstants}
                                    />
                                )}
                            />
                            <Route
                                path="/:dashboardKey/:viewKey?"
                                render={({ location, match }) => (
                                    <DashboardLayout
                                        assets={props.assets}
                                        customizations={props.customizations}
                                        dashboardKey={match.params.dashboardKey}
                                        favoriteUtilities={
                                            props.favoriteUtilities
                                        }
                                        location={location}
                                        siteLanguage={props.siteLanguage}
                                        uriConstants={props.uriConstants}
                                        viewKey={match.params.viewKey}
                                    />
                                )}
                            />
                        </Switch>
                    </BrowserRouter>
                </FeatureTokenContextProvider>
            </LabelContextProvider>
        </QueryClientProvider>
    );
}

AppRouter.propTypes = {
    assets: PropTypes.instanceOf(Object),
    baseName: PropTypes.string.isRequired,
    customizations: PropTypes.instanceOf(Object),
    favoriteUtilities: PropTypes.instanceOf(Object).isRequired,
    menuUtilities: PropTypes.instanceOf(Object),
    siteLanguage: PropTypes.string,
    uriConstants: PropTypes.instanceOf(Object).isRequired,
    ssiSystemFeature: PropTypes.shape({
        header: PropTypes.bool,
        footer: PropTypes.bool,
    }),
};

AppRouter.defaultProps = {
    assets: {},
    customizations: {},
    menuUtilities: {
        saveUserDashboard: () => {},
        deleteUserDashboard: () => {},
    },
    siteLanguage: 'en',
};
